import { useState, useEffect } from 'react'
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap'
import { 
  useGetSettingsDefinitionsQuery,
  useGetAllSettingsQuery,
  useUpdateSettingMutation, 
  Setting 
} from '../../services/settings.service'

function SettingsManagement(): JSX.Element {
  const { data: settingsDefinitions, isLoading: isLoadingDefinitions, error: definitionsError } = useGetSettingsDefinitionsQuery({})
  const { data: allSettings, isLoading: isLoadingSettings, error: settingsError, refetch: refetchSettings } = useGetAllSettingsQuery({})
  const [updateSetting, { isLoading: isUpdating }] = useUpdateSettingMutation()
  const [updatedSettings, setUpdatedSettings] = useState<Record<string, string>>({})
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [mergedSettings, setMergedSettings] = useState<Setting[]>([])

  const handleInputChange = (id: string, value: string) => {
    setUpdatedSettings({
      ...updatedSettings,
      [id]: value
    })
    setSaveSuccess(false)
  }

  const handleCheckboxChange = (id: string, checked: boolean) => {
    setUpdatedSettings({
      ...updatedSettings,
      [id]: checked ? 'true' : 'false'
    })
    setSaveSuccess(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaveSuccess(false)
    setSaveError(null)
    
    try {
      const updatePromises = Object.entries(updatedSettings).map(([id, value]) => 
        updateSetting({ id, value }).unwrap()
      )
      
      await Promise.all(updatePromises)
      setSaveSuccess(true)
      setUpdatedSettings({})
      refetchSettings()
    } catch (err) {
      setSaveError('Failed to save settings. Please try again.')
      console.error('Error updating settings:', err)
    }
  }

  // Merge settings from database with definitions
  useEffect(() => {
    console.log('settingsDefinitions', settingsDefinitions)
    console.log('allSettings', allSettings)
    if (settingsDefinitions && allSettings) {
      // Create a map of existing settings
      const settingsMap = new Map<string, string>();
      (allSettings as any).forEach((setting: any) => {
        settingsMap.set(setting.id, setting.value);
      });
      
      // Ensure settingsDefinitions is an array before mapping
      if (Array.isArray(settingsDefinitions)) {
        // Merge with definitions
        const merged = settingsDefinitions.map(setting => ({
          ...setting,
          // Use the value from database if exists, otherwise use the default from definition
          value: settingsMap.has(setting.id) ? settingsMap.get(setting.id)! : setting.value
        }));
        
        setMergedSettings(merged);
      } else {
        console.error('settingsDefinitions is not an array:', settingsDefinitions);
        // If settingsDefinitions is not an array, set an empty array as mergedSettings
        setMergedSettings([]);
      }
    }
  }, [settingsDefinitions, allSettings]);

  const getValue = (setting: Setting) => {
    return updatedSettings[setting.id] !== undefined 
      ? updatedSettings[setting.id] 
      : setting.value
  }

  const renderSettingInput = (setting: Setting) => {
    const value = getValue(setting)
    
    // Debug logging for LLM model setting
    if (setting.id === 'default_LLM_model') {
      console.log('LLM Model setting:', setting);
      console.log('inputType:', setting.inputType);
      console.log('options:', setting.options);
    }
    
    switch (setting.inputType) {
      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={value}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
          />
        )
      case 'flag':
        return (
          <Form.Check
            type="switch"
            checked={value === 'true'}
            onChange={(e) => handleCheckboxChange(setting.id, e.target.checked)}
          />
        )
      case 'select':
        return (
          <Form.Select
            value={value}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
          >
            {setting.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        )
      case 'text':
      default:
        return (
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
          />
        )
    }
  }

  if (isLoadingDefinitions || isLoadingSettings) {
    return (
      <Container className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  if (definitionsError || settingsError) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Failed to load settings. Please refresh the page or contact support.
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <h2 className="mb-4">System Settings</h2>
      
      {saveSuccess && (
        <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)}>
          Settings saved successfully!
        </Alert>
      )}
      
      {saveError && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {Array.isArray(mergedSettings) && mergedSettings.length > 0 ? (
              mergedSettings.map((setting) => (
                <Form.Group className="mb-3" key={setting.id}>
                  <Form.Label>
                    {setting.label || setting.id.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </Form.Label>
                  {renderSettingInput(setting)}
                </Form.Group>
              ))
            ) : (
              <Alert variant="info">No settings available.</Alert>
            )}
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isUpdating || Object.keys(updatedSettings).length === 0}
              >
                {isUpdating ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default SettingsManagement
